// Events query layer. No 'use client' directive — server pages import the
// keys/fetchers for prefetch wiring; client components call the hooks.

import { useMemo } from 'react';
import {
  keepPreviousData,
  queryOptions,
  infiniteQueryOptions,
  useQuery,
  useInfiniteQuery,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError, unwrap } from '../api-error';
import { QUERY_KEY_REGISTRY } from './normalize';

export type EventSummary = components['schemas']['EventSummary'];
export type EventDetail = components['schemas']['EventDetail'];
export type RsvpView = components['schemas']['RsvpView'];
export type UpcomingMeetingView = components['schemas']['UpcomingMeetingView'];
export type UpcomingMeetingsResponse = components['schemas']['UpcomingMeetingsResponse'];
export type PublicEventCard = components['schemas']['PublicEventCard'];
export type PublicEventsPage = components['schemas']['PublicEventsPage'];
export type PublicEventFacets = components['schemas']['PublicEventFacets'];

export type ListEventsInput = {
  scope?: 'mine' | 'circle' | 'network' | 'platform';
  window?: 'upcoming' | 'past';
  pageSize?: number;
  cursor?: string;
  from?: string;
  to?: string;
  placeText?: string;
  placeCountryCode?: string;
  tag?: string;
  q?: string;
  groupId?: string;
  format?: 'in_person' | 'virtual';
};

export type EventsPage = {
  items: EventSummary[];
  nextCursor: string | null;
  pageSize: number;
};

// ---- Client fetchers ----

export async function fetchEvents(
  input: ListEventsInput = {},
): Promise<EventsPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/events', {
    params: {
      query: {
        scope: input.scope,
        window: input.window,
        pageSize: input.pageSize,
        cursor: input.cursor,
        from: input.from,
        to: input.to,
        placeText: input.placeText,
        placeCountryCode: input.placeCountryCode,
        tag: input.tag,
        q: input.q,
        groupId: input.groupId,
        format: input.format,
      },
    },
  }));
  return data;
}

// ---- Signed-out public directory ----

export type ListPublicEventsInput = {
  q?: string;
  from?: string;
  to?: string;
  format?: 'online' | 'in_person';
  countryCode?: string;
  topic?: string;
  cursor?: string;
  pageSize?: number;
};

const PUBLIC_EVENTS_PAGE_SIZE = 24;

export async function fetchPublicEvents(
  input: ListPublicEventsInput,
): Promise<PublicEventsPage> {
  const client = getApiClient();
  return unwrap(await client.GET('/api/v1/public-events', {
    params: { query: input },
  }));
}

export async function fetchPublicEventFacets(): Promise<PublicEventFacets> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/public-events/facets'));
  return data.data;
}

export async function fetchEventDetail(id: string): Promise<EventDetail> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/events/{id}', {
    params: { path: { id } },
  }));
  return data.data;
}

export async function fetchEventAttendees(
  id: string,
  limit = 6,
): Promise<readonly RsvpView[]> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/events/{id}/attendees',
    {
      params: { path: { id }, query: { pageSize: limit } },
    },
  );
  if (data === undefined) {
    // Non-organizers get 403 — return empty preview rather than throwing,
    // mirroring the server-side `getEventAttendeePreview` tolerance.
    if (response.status === 403 || response.status === 404) return [];
    throwApiError(error, response);
  }
  return data.items;
}

// ---- URL search → list input (single source of truth) ----
//
// Shape of the events index URL. Tolerates partials/raw strings so the route's
// `validateSearch` output and the body's `useFilterParams` `values` (which
// already narrows to known enums) are both assignable.

export type EventsListFormat = 'in_person' | 'virtual';
export type EventsListTab = 'mine' | 'circle' | 'network' | 'platform';

export type EventsSearchInput = {
  q?: string;
  tab?: EventsListTab;
  when?: 'upcoming' | 'past';
  format?: EventsListFormat | 'any';
  from?: string;
  to?: string;
};

const EVENTS_DEFAULT_PAGE_SIZE = 25;

function eventDateToIsoStart(d: string): string | undefined {
  const parsed = new Date(`${d}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function eventDateToIsoEnd(d: string): string | undefined {
  const parsed = new Date(`${d}T23:59:59Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

// Normalizes the events-index URL search into a list input. Runs INSIDE the
// list factory leaf (decision 3) so route loaders and the hook share a key:
// trims `q`, converts `from`/`to` (date strings) → ISO, fills the default
// page size. Loaders bypass hooks, so this cannot live in the hook.
export function eventsListInputFromSearch(
  search: EventsSearchInput,
): Omit<ListEventsInput, 'cursor'> {
  const tab: EventsListTab = search.tab ?? 'mine';
  const q = (search.q ?? '').trim();
  const format: EventsListFormat | 'any' = search.format ?? 'any';
  const from = search.from ?? '';
  const to = search.to ?? '';
  const fromIso = from === '' ? undefined : eventDateToIsoStart(from);
  const toIso = to === '' ? undefined : eventDateToIsoEnd(to);
  return {
    scope: tab,
    pageSize: EVENTS_DEFAULT_PAGE_SIZE,
    ...(search.when === 'past' ? { window: 'past' as const } : {}),
    ...(q.length > 0 ? { q } : {}),
    ...(format !== 'any' ? { format } : {}),
    ...(fromIso !== undefined ? { from: fromIso } : {}),
    ...(toIso !== undefined ? { to: toIso } : {}),
  };
}

// ---- My upcoming meetings (feed rail) ----

// Rail shows up to 3 (UpcomingEventsSection cap). One extra so the section
// can detect overflow and surface the "View all events →" footer.
const RAIL_PAGE_SIZE = 4;

// Home-rail bucket: 'circle' = circle-hosted events, 'network' = everything
// else (networks, spaces, platform calendars). Each rail module fetches its
// own bucket so a run of same-kind events can't starve the other module.
export type MeetingKindFilter = 'circle' | 'network';

export interface MyUpcomingMeetingsInput {
  pageSize?: number;
  kind?: MeetingKindFilter;
}

export async function fetchMyUpcomingMeetings(
  input: MyUpcomingMeetingsInput = {},
): Promise<UpcomingMeetingsResponse> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/me/meetings', {
    params: {
      query: {
        pageSize: input.pageSize ?? RAIL_PAGE_SIZE,
        ...(input.kind !== undefined ? { kind: input.kind } : {}),
      },
    },
  }));
  return data;
}

// ---- Event-create option fetchers (consumed by events/new route) ----

export type EventCreateGroupOption = {
  id: string;
  label: string;
  defaultCalendarId: string | null;
};

export type PlatformCalendarOption =
  components['schemas']['PlatformCalendarsResponse']['items'][number];

export async function fetchMyLeadingCircleOptions(): Promise<
  readonly EventCreateGroupOption[]
> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/members/me/circles', {
    params: { query: { pageSize: 50, role: 'lead' } },
  }));
  return data.items.map((c) => ({
    id: c.id,
    label: c.name,
    defaultCalendarId: c.defaultCalendarId ?? null,
  }));
}

// Calls /api/v1/members/me/networks directly rather than importing the groups
// fetcher: events.ts must not depend on groups.ts. The dependency runs one way
// — groups composes eventsKeys.lists() from here — so pulling a fetcher back
// would form an import cycle (and crash module init on the shared prefix).
export async function fetchMyLeadingNetworkOptions(): Promise<
  readonly EventCreateGroupOption[]
> {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/members/me/networks',
  );
  if (response.status === 403 || response.status === 404) return [];
  if (error !== undefined || data === undefined) throwApiError(error, response);
  return data.items
    .filter((n) => n.viewerMembership === 'leader')
    .map((n) => ({
      id: n.id,
      label: n.name,
      defaultCalendarId:
        (n as { defaultCalendarId?: string | null }).defaultCalendarId ?? null,
    }));
}

export async function fetchPlatformCalendars(): Promise<
  readonly PlatformCalendarOption[]
> {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/calendars/platform');
  return data?.items ?? [];
}

// ---- Query keys + leaf factories ----
//
// Inner nodes (`all`, `lists`) are bare `as const` prefix arrays used by the
// mutation layer for partial-match invalidation; they are NOT leaves. Every
// leaf returns a queryOptions/infiniteQueryOptions object built from one
// normalized `args`. `lists()` and the `detail` prefix are a STABLE shared
// contract — the groups domain composes `eventsKeys.lists()` for its
// circle/network event leaves. Do not rename or restructure them.

export const eventsKeys = {
  all: ['events'] as const,
  lists: () => [...eventsKeys.all, 'list'] as const,

  // Infinite list leaf — cursor injected via pageParam, never in the key.
  // `args` is the terminal element and the sole fetch input besides the
  // cursor. Normalization happens here so loaders and the hook share a key.
  list: (raw: Omit<ListEventsInput, 'cursor'> = {}) => {
    const args: Omit<ListEventsInput, 'cursor'> = { ...raw };
    return infiniteQueryOptions({
      queryKey: [...eventsKeys.lists(), args] as const,
      queryFn: ({ pageParam }) =>
        fetchEvents({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  // The signed-out directory. Under lists() so the invalidation an event
  // create/edit/cancel sends to every events list reaches it too.
  publicList: (raw: Omit<ListPublicEventsInput, 'cursor'> = {}) => {
    const { q: rawQ, pageSize, ...filters } = raw;
    const q = rawQ?.trim() ?? '';
    const args: Omit<ListPublicEventsInput, 'cursor'> = {
      ...filters,
      pageSize: pageSize ?? PUBLIC_EVENTS_PAGE_SIZE,
      ...(q.length > 0 ? { q } : {}),
    };
    return infiniteQueryOptions({
      queryKey: [...eventsKeys.lists(), 'public', args] as const,
      queryFn: ({ pageParam }) =>
        fetchPublicEvents({ ...args, ...(pageParam !== undefined ? { cursor: pageParam } : {}) }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  },

  publicFacets: () =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'public-facets'] as const,
      queryFn: fetchPublicEventFacets,
    }),

  // Keyed under lists() so useRsvpEvent.onSettled invalidation reaches the rail.
  myUpcomingMeetings: (raw: MyUpcomingMeetingsInput = {}) => {
    const args: MyUpcomingMeetingsInput = {
      pageSize: raw.pageSize ?? RAIL_PAGE_SIZE,
      ...(raw.kind !== undefined ? { kind: raw.kind } : {}),
    };
    return queryOptions({
      queryKey: [...eventsKeys.lists(), 'my-upcoming-meetings', args] as const,
      queryFn: () => fetchMyUpcomingMeetings(args),
    });
  },

  detail: (id: string) =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'detail', id] as const,
      queryFn: () => fetchEventDetail(id),
    }),

  attendees: (id: string, limit?: number) =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'attendees', id, { limit }] as const,
      queryFn: () => fetchEventAttendees(id, limit),
    }),

  // Wholly-local event-create option leaves (consumed by events/new route).
  myLeadingCircleOptions: () =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'my-leading-circle-options'] as const,
      queryFn: fetchMyLeadingCircleOptions,
    }),

  myLeadingNetworkOptions: () =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'my-leading-network-options'] as const,
      queryFn: fetchMyLeadingNetworkOptions,
    }),

  platformCalendars: () =>
    queryOptions({
      queryKey: [...eventsKeys.all, 'platform-calendars'] as const,
      queryFn: fetchPlatformCalendars,
    }),
};

QUERY_KEY_REGISTRY.push(
  { name: 'eventsKeys.list', sampleKey: eventsKeys.list({ scope: 'mine', pageSize: 25 }).queryKey },
  { name: 'eventsKeys.publicList', sampleKey: eventsKeys.publicList({ q: 'mentor' }).queryKey },
  { name: 'eventsKeys.publicFacets', sampleKey: eventsKeys.publicFacets().queryKey },
  { name: 'eventsKeys.myUpcomingMeetings', sampleKey: eventsKeys.myUpcomingMeetings({ pageSize: 4, kind: 'circle' }).queryKey },
  { name: 'eventsKeys.detail', sampleKey: eventsKeys.detail('evt_1').queryKey },
  { name: 'eventsKeys.attendees', sampleKey: eventsKeys.attendees('evt_1', 6).queryKey },
  { name: 'eventsKeys.myLeadingCircleOptions', sampleKey: eventsKeys.myLeadingCircleOptions().queryKey },
  { name: 'eventsKeys.myLeadingNetworkOptions', sampleKey: eventsKeys.myLeadingNetworkOptions().queryKey },
  { name: 'eventsKeys.platformCalendars', sampleKey: eventsKeys.platformCalendars().queryKey },
);

// ---- Hooks ----

export function useInfiniteEvents(input: Omit<ListEventsInput, 'cursor'>) {
  return useInfiniteQuery(eventsKeys.list(input));
}

export function useInfiniteEventsFromSearch(search: EventsSearchInput) {
  const input = useMemo(() => eventsListInputFromSearch(search), [search]);
  return useInfiniteEvents(input);
}

export function useInfinitePublicEvents(input: Omit<ListPublicEventsInput, 'cursor'>) {
  return useInfiniteQuery(eventsKeys.publicList(input));
}

export function usePublicEventFacets() {
  return useQuery(eventsKeys.publicFacets());
}

export function useEventDetail(id: string) {
  return useQuery(eventsKeys.detail(id));
}

export function useEventAttendees(id: string, limit?: number) {
  return useQuery(eventsKeys.attendees(id, limit));
}

export function useMyUpcomingMeetings(input: MyUpcomingMeetingsInput = {}) {
  return useQuery(eventsKeys.myUpcomingMeetings(input));
}
